// web/src/shell/ShellContext.tsx — app-shell level state shared between the
// TopBar, SideNav and the routed pages (M0.4). Holds the resolved principal,
// theme + toggle, the act-as identity switcher, and realtime connection status.
// The data-bearing console route still owns its own list state; this only
// carries cross-cutting shell concerns.
import React, { createContext, useContext } from 'react';
import type { Principal } from '../../../shared/types';
import type { ActAsId } from '../auth';

export interface ShellState {
  principal: Principal;
  theme: string;
  toggleTheme: () => void;
  actAs: ActAsId;
  switchActAs: (id: ActAsId) => void;
  wsConnected: boolean;
  /** Deployment environment chip label, e.g. "preview" / "production". */
  environment: string;
  /** Opens the ⌘K command palette (wired by the shell; M4.4). */
  openPalette?: () => void;
}

const ShellContext = createContext<ShellState | null>(null);

export function ShellProvider({
  value,
  children,
}: {
  value: ShellState;
  children: React.ReactNode;
}): React.JSX.Element {
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellState {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell must be used within a ShellProvider');
  return ctx;
}
